import { FiberManualRecord } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { alertTypeLabelKey, MODEL_CHART_COLORS, MODELS } from "config/constants";
import useTranslation from "hooks/useTranslation";
import { useMemo } from "react";
import Chart from "react-apexcharts";
import { ModelTypes, ThreatModel } from "types";
import { collapseSharedFeatureModels, countThreatsForDisplayModel } from "./displayModels";

interface IPie {
  threats?: ThreatModel[];
  loading?: boolean;
  period: string;
  models?: ModelTypes[];
}

function DonutCenter({ value, label }: { value: string; label: string }) {
  return (
    <Box
      position="absolute"
      sx={{
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        pointerEvents: "none",
        zIndex: 1,
        px: 2,
        transform: "translateY(-5px)"
      }}
    >
      <Typography fontWeight={700} fontSize={18} color={COLORS.surface.primaryText} lineHeight={1} align="center">
        {value}
      </Typography>
      <Typography fontSize={10} color={COLORS.surface.mutedText} lineHeight={1} mt={0.5} align="center">
        {label}
      </Typography>
    </Box>
  );
}

export default function Pie({ period, loading, threats, models = [] }: IPie) {
  const t = useTranslation();
  const displayModels = useMemo(() => collapseSharedFeatureModels(models), [models]);
  const modelIndexes = displayModels.map((model) => MODELS.indexOf(model));
  const selectedModelLabels = displayModels.map((model) => alertTypeLabelKey(model));
  const series = useMemo(() => {
    return displayModels.map((model) => countThreatsForDisplayModel(threats, model, models));
  }, [threats, models, displayModels]);

  const chartColors = modelIndexes.map((i) => MODEL_CHART_COLORS[i]);
  const total = useMemo(() => series.reduce((sum, n) => sum + n, 0), [series]);
  const hasData = total > 0;
  const monitoredLabel = t["Monitored"] || "Monitored";
  const centerValue = hasData ? "100%" : "0%";

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: `${COLORS.radius.lg}px`,
        bgcolor: COLORS.surface.card,
        border: `1px solid ${COLORS.surface.border}`,
        boxShadow: COLORS.shadow.card
      }}
    >
      <CardHeader
        title={t["Threshold Comparison"]}
        subheader={t["% composition of threat levels."]}
        action={
          <Typography
            variant="caption"
            sx={{ color: COLORS.surface.mutedText, fontWeight: 600, pt: 0.5, fontSize: 12 }}
          >
            {period}
          </Typography>
        }
        titleTypographyProps={{
          fontWeight: 600,
          sx: { color: COLORS.surface.primaryText, fontSize: 16 },
          letterSpacing: "-0.01em"
        }}
        subheaderTypographyProps={{
          sx: { color: COLORS.surface.secondaryText, fontSize: 13, mt: 0.25 }
        }}
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 0,
          alignItems: "flex-start"
        }}
      />
      <CardContent sx={{ px: 2.5, pt: 2, pb: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Grid container alignItems="center" spacing={1.5}>
          <Grid item xs={12} sm={5}>
            {loading ? (
              <Box width="100%" height={180} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : hasData ? (
              <Box
                mx="auto"
                position="relative"
                sx={{
                  width: 180,
                  height: 180,
                  "& .apexcharts-canvas": { margin: "0 auto" }
                }}
              >
                <Chart
                  series={series}
                  type="donut"
                  width={180}
                  height={180}
                  options={{
                    chart: {
                      toolbar: { show: false },
                      width: 180,
                      height: 180
                    },
                    stroke: { width: 0 },
                    labels: selectedModelLabels.map((label) => t[`${label}`] || label),
                    fill: { colors: chartColors },
                    colors: chartColors,
                    legend: { show: false },
                    dataLabels: { enabled: false },
                    plotOptions: {
                      pie: {
                        customScale: 1,
                        donut: {
                          size: "78%",
                          labels: {
                            show: false
                          }
                        }
                      }
                    }
                  }}
                />
                <DonutCenter value={centerValue} label={monitoredLabel} />
              </Box>
            ) : (
              <Box
                mx="auto"
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 180,
                  height: 180,
                  borderRadius: "50%",
                  border: `14px solid ${COLORS.surface.track}`,
                  boxSizing: "border-box"
                }}
              >
                <DonutCenter value={centerValue} label={monitoredLabel} />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={7}>
            <List dense disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1.25, pl: { sm: 1 } }}>
              {selectedModelLabels.map((label, i) => (
                <ListItem key={displayModels[i]} disableGutters sx={{ py: 0, px: 0, gap: 1 }}>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <FiberManualRecord sx={{ fontSize: 8 }} htmlColor={chartColors[i]} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t[`${label}`]}
                    primaryTypographyProps={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: COLORS.surface.secondaryText
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
